import React, { useState } from "react";
import { Link } from "react-router-dom";
import EngineeringIcon from "./EngineeringIcons";
import AnimatedDivider from "./AnimatedDivider";

interface OngoingProject {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  client: string;
  budget: string;
  startDate: string;
  expectedCompletion: string;
  progress: number;
  technologies: string[];
  currentPhase: string;
  nextMilestone: string;
  team: string[];
  specifications: {
    title: string;
    value: string;
    unit?: string;
  }[];
  challenges: string[];
  solutions: string[];
}

const OngoingProjects: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const ongoingProjects: OngoingProject[] = [
    {
      id: "2",
      title: "Solar-Powered Irrigation System",
      category: "Energy Engineering",
      description: "Large-scale solar-powered irrigation system for agricultural development with smart automation.",
      image: "/project2.png",
      client: "Ekumfi Agricultural Development Authority",
      budget: "GHS 12,000",
      startDate: "September 2023",
      expectedCompletion: "May 2024",
      progress: 75,
      technologies: ["Solar Power Systems", "Smart Irrigation", "IoT Sensors", "Mobile Monitoring", "Automated Controls"],
      currentPhase: "Automation Integration",
      nextMilestone: "Final Testing - March 2024",
      team: ["Michael Kofi", "Abena Osei", "Kwesi Addo", "Nana Yaa"],
      specifications: [
        { title: "Solar Capacity", value: "500", unit: "kW" },
        { title: "Irrigation Coverage", value: "200", unit: "hectares" },
        { title: "Water Savings", value: "80", unit: "%" }
      ],
      challenges: [
        "Large-scale solar system integration",
        "Weather-based automation optimization",
        "Remote monitoring system setup"
      ],
      solutions: [
        "Implemented modular solar power system",
        "Developed weather-responsive automation",
        "Created mobile monitoring application"
      ]
    },
    {
      id: "industrial-drying-system",
      title: "Industrial Drying System",
      category: "Farm Machinery & Food Processing",
      description: "Industrial-grade drying machine for agricultural products processing with energy-efficient operation.",
      image: "/drying_machine.png",
      client: "Ekumfi Agricultural Processing Center",
      budget: "GHS 11,000",
      startDate: "January 2024",
      expectedCompletion: "August 2024",
      progress: 45,
      technologies: ["Thermal Management", "Energy Recovery", "Digital Controls", "IoT Monitoring", "Multi-Chamber Design"],
      currentPhase: "Fabrication",
      nextMilestone: "Installation - June 2024",
      team: ["Sarah Mensah", "David Osei", "Grace Addo", "Kwame Asante"],
      specifications: [
        { title: "Drying Capacity", value: "5", unit: "tons/day" },
        { title: "Energy Efficiency", value: "60", unit: "% improvement" },
        { title: "Chambers", value: "6", unit: "units" }
      ],
      challenges: [
        "Maintaining consistent temperature across multiple chambers",
        "Optimizing energy consumption for large-scale operations",
        "Integrating energy recovery systems"
      ],
      solutions: [
        "Implemented advanced thermal management system",
        "Designed energy recovery and heat exchange systems",
        "Integrated real-time monitoring and adjustment"
      ]
    },
    {
      id: "smart-farming-automation",
      title: "Smart Farming Automation System",
      category: "Farm Machinery & Food Processing",
      description: "Comprehensive smart farming automation system with AI-powered crop management and monitoring.",
      image: "/project5.png",
      client: "Ekumfi Smart Agriculture Cooperative",
      budget: "GHS 15,000",
      startDate: "February 2024",
      expectedCompletion: "July 2024",
      progress: 30,
      technologies: ["AI & Machine Learning", "IoT Sensors", "Automated Equipment", "Data Analytics", "Mobile Apps"],
      currentPhase: "System Design",
      nextMilestone: "Prototype Development - April 2024",
      team: ["Nana Yaa", "Michael Kofi", "Abena Osei", "Kwesi Addo"],
      specifications: [
        { title: "Coverage Area", value: "500", unit: "hectares" },
        { title: "Automation Level", value: "90", unit: "%" },
        { title: "Data Points", value: "1000+", unit: "sensors" }
      ],
      challenges: [
        "AI algorithm development for crop management",
        "Integration of multiple sensor systems",
        "Real-time data processing and analysis"
      ],
      solutions: [
        "Developed custom AI algorithms for crop optimization",
        "Implemented comprehensive IoT sensor network",
        "Created real-time data processing platform"
      ]
    },
    {
      id: "renewable-energy-hub",
      title: "Renewable Energy Hub",
      category: "Energy Engineering",
      description: "Integrated renewable energy hub combining solar, wind, and biomass energy systems.",
      image: "/project6.png",
      client: "Ekumfi Energy Cooperative",
      budget: "GHS 20,000",
      startDate: "March 2024",
      expectedCompletion: "October 2024",
      progress: 20,
      technologies: ["Solar Power", "Wind Energy", "Biomass Systems", "Energy Storage", "Smart Grid"],
      currentPhase: "Site Preparation",
      nextMilestone: "Solar Installation - May 2024",
      team: ["Kwame Asante", "Sarah Mensah", "David Osei", "Grace Addo"],
      specifications: [
        { title: "Total Capacity", value: "1", unit: "MW" },
        { title: "Energy Storage", value: "500", unit: "kWh" },
        { title: "Grid Integration", value: "100", unit: "%" }
      ],
      challenges: [
        "Integration of multiple energy sources",
        "Energy storage system optimization",
        "Grid connection and stability"
      ],
      solutions: [
        "Designed hybrid energy management system",
        "Implemented advanced energy storage solutions",
        "Developed smart grid integration protocols"
      ]
    },
    {
      id: "advanced-welding-facility",
      title: "Advanced Welding & Fabrication Facility",
      category: "Manufacturing",
      description: "State-of-the-art welding and fabrication facility with automated production lines.",
      image: "/project1.png",
      client: "Ekumfi Manufacturing Cooperative",
      budget: "GHS 18,000",
      startDate: "January 2024",
      expectedCompletion: "September 2024",
      progress: 60,
      technologies: ["Automated Welding", "Robotic Assembly", "Quality Control", "3D Printing", "CAD/CAM"],
      currentPhase: "Equipment Installation",
      nextMilestone: "Production Testing - June 2024",
      team: ["Grace Addo", "Michael Kofi", "Nana Yaa", "Kwesi Addo"],
      specifications: [
        { title: "Production Capacity", value: "100", unit: "units/day" },
        { title: "Precision Level", value: "99.9", unit: "%" },
        { title: "Automation Level", value: "85", unit: "%" }
      ],
      challenges: [
        "Integration of robotic welding systems",
        "Quality control automation",
        "Production line optimization"
      ],
      solutions: [
        "Implemented advanced robotic welding systems",
        "Designed automated quality control processes",
        "Optimized production line efficiency"
      ]
    }
  ];

  const categories = [
    { id: "all", name: "All Projects", icon: "🏗️" },
    { id: "Energy Engineering", name: "Energy Engineering", icon: "⚡" },
    { id: "Farm Machinery & Food Processing", name: "Farm Machinery", icon: "🚜" },
    { id: "Manufacturing", name: "Manufacturing", icon: "🏭" }
  ];

  const filteredProjects = ongoingProjects.filter(project => {
    const matchesCategory = selectedCategory === "all" || project.category === selectedCategory;
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const stats = {
    totalProjects: ongoingProjects.length,
    totalBudget: ongoingProjects.reduce((sum, project) => sum + parseFloat(project.budget.replace(/[^\d.]/g, '')), 0),
    avgProgress: Math.round(ongoingProjects.reduce((sum, project) => sum + project.progress, 0) / ongoingProjects.length),
    totalJobs: ongoingProjects.reduce((sum, project) => sum + project.team.length, 0)
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return "bg-green-500";
    if (progress >= 60) return "bg-blue-500";
    if (progress >= 40) return "bg-yellow-500";
    return "bg-orange-500";
  };

  const getProgressText = (progress: number) => {
    if (progress >= 80) return "Near Completion";
    if (progress >= 60) return "Good Progress";
    if (progress >= 40) return "In Progress";
    return "Early Stage";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-pulse">
                <EngineeringIcon type="pulley" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Ongoing Projects</h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Witness our engineering excellence in action. These projects are currently transforming 
              industries and communities across Ekumfiman with cutting-edge technology and innovation.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.totalProjects}</div>
                <div className="text-blue-200">Active Projects</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">GHS {stats.totalBudget.toLocaleString()}</div>
                <div className="text-blue-200">Total Investment</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.avgProgress}%</div>
                <div className="text-blue-200">Average Progress</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">{stats.totalJobs}</div>
                <div className="text-blue-200">Team Members</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Filters and Search */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative max-w-md mx-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EngineeringIcon type="wrench" size="sm" className="text-gray-400" animated={false} />
                </div>
                <input
                  type="text"
                  placeholder="Search ongoing projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${
                    selectedCategory === category.id
                      ? "bg-blue-500 text-white shadow-lg transform scale-105"
                      : "bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700"
                  }`}
                >
                  <span className="text-lg">{category.icon}</span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Projects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {filteredProjects.length === 0 ? (
              <div className="text-center py-16">
                <EngineeringIcon type="cog" size="lg" className="text-gray-400 mx-auto mb-4" animated={false} />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No projects found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {filteredProjects.map((project) => (
                  <div key={project.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 engineering-card">
                    {/* Project Image */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center justify-between">
                          <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                            In Progress
                          </span>
                          <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                            {project.expectedCompletion}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="p-6">
                      {/* Category */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">⚡</span>
                        <span className="text-sm text-gray-500 font-medium">{project.category}</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                        {project.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {project.description}
                      </p>

                      {/* Progress Bar */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-700">Progress</span>
                          <span className="text-sm font-bold text-blue-600">{project.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-1000 ${getProgressColor(project.progress)}`}
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">{getProgressText(project.progress)}</span>
                          <span className="text-xs text-gray-500">{project.currentPhase}</span>
                        </div>
                      </div>

                      {/* Key Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-blue-600">{project.budget}</div>
                          <div className="text-xs text-gray-500">Budget</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-lg font-bold text-blue-600">{project.team.length}</div>
                          <div className="text-xs text-gray-500">Team Members</div>
                        </div>
                      </div>

                      {/* Current Phase & Next Milestone */}
                      <div className="mb-6 space-y-3">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h4 className="text-sm font-semibold text-blue-700 mb-1 flex items-center gap-2">
                            <EngineeringIcon type="bolt" size="sm" className="text-blue-500" animated={false} />
                            Current Phase
                          </h4>
                          <p className="text-sm text-blue-600">{project.currentPhase}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <h4 className="text-sm font-semibold text-green-700 mb-1 flex items-center gap-2">
                            <EngineeringIcon type="gear" size="sm" className="text-green-500" animated={false} />
                            Next Milestone
                          </h4>
                          <p className="text-sm text-green-600">{project.nextMilestone}</p>
                        </div>
                      </div>

                      {/* Technologies */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <EngineeringIcon type="wrench" size="sm" className="text-blue-500" animated={false} />
                          Technologies
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {project.technologies.slice(0, 3).map((tech, index) => (
                            <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                              {tech}
                            </span>
                          ))}
                          {project.technologies.length > 3 && (
                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                              +{project.technologies.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Key Specifications */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <EngineeringIcon type="screw" size="sm" className="text-blue-500" animated={false} />
                          Key Specifications
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {project.specifications.slice(0, 2).map((spec, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-600">{spec.title}</span>
                              <span className="font-semibold text-gray-800">
                                {spec.value}{spec.unit}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Button */}
                      <Link
                        to={`/projects/${project.id}`}
                        className="block w-full bg-blue-500 text-white text-center py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <EngineeringIcon type="pulley" size="sm" animated={false} />
                        View Project Details
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Join Our Engineering Journey</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Be part of our ongoing mission to industrialize Ekumfiman through engineering excellence. 
            Let's create the future together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacts"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <EngineeringIcon type="wrench" size="sm" animated={false} />
              Start Your Project
            </Link>
            <Link
              to="/projects/completed"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <EngineeringIcon type="gear" size="sm" />
              View Completed Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OngoingProjects;
